import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  Menu, 
  X,
  Sun,
  Moon
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export const Header: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { theme, toggleTheme } = useTheme();

  const isHomePage = location.pathname === '/';

  const navigation = [
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Documents', href: '/documents' },
    { name: 'Study Tools', href: '/study' },
    { name: 'Progress', href: '/progress' },
  ];

  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <header className={`${
      isHomePage 
        ? 'bg-transparent backdrop-blur-sm border-gray-700/30 absolute top-0 left-0 right-0 z-50' 
        : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 relative'
    } shadow-sm border-b h-14 flex items-center`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-full">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="p-1.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg">
                <GraduationCap className="h-5 w-5 text-white" />
              </div>
              <span className={`text-lg font-heading font-bold ${
                isHomePage ? 'text-white' : 'text-gray-900 dark:text-white'
              }`}>
                EcstaticLearn
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-2 py-1 text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? isHomePage 
                      ? 'text-purple-300' 
                      : 'text-primary-600 dark:text-primary-400'
                    : isHomePage
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {item.name}
                {location.pathname === item.href && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className={`absolute -bottom-px left-0 right-0 h-0.5 ${
                      isHomePage ? 'bg-purple-300' : 'bg-primary-600 dark:bg-primary-400'
                    }`}
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3">
            {/* Theme Toggle - Only show when NOT on home page */}
            {!isHomePage && (
              <button
                onClick={toggleTheme}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden p-1.5 transition-colors rounded-lg ${
                isHomePage
                  ? 'text-gray-300 hover:text-white hover:bg-white/10'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation + Backdrop */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={() => setIsMenuOpen(false)}
            />
            {/* Mobile Menu */}
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={`fixed top-14 left-0 right-0 z-50 w-full bg-white dark:bg-gray-900 shadow-lg border-t border-gray-200 dark:border-gray-700 md:hidden py-3`}
            >
              <div className="space-y-1 px-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-3 py-2 min-h-[44px] min-w-[44px] text-sm font-medium rounded-md transition-colors ${
                      location.pathname === item.href
                        ? isHomePage
                          ? 'text-purple-300 bg-purple-900/20'
                          : 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                        : isHomePage
                          ? 'text-gray-300 hover:text-white hover:bg-white/10'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                    style={{ touchAction: 'manipulation' }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                {/* Theme Toggle in Mobile Menu - Only show when NOT on home page */}
                {!isHomePage && (
                  <button
                    onClick={toggleTheme}
                    className="w-full text-left px-3 py-2 min-h-[44px] min-w-[44px] text-sm font-medium rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 flex items-center space-x-2"
                    style={{ touchAction: 'manipulation' }}
                  >
                    {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                    <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </div>
    </header>
  );
};